import React, { useEffect, useState } from 'react'
import axios, { AxiosResponse } from 'axios'
import { motion, AnimatePresence } from 'framer-motion'
import { FaArrowLeft, FaTimes, FaCheck } from 'react-icons/fa'

import Input from './components/Input'
import Button from './components/Button'
import Loader from './components/Loader'
import Modal from "./components/Modal";

import './Login.scss'
import useCookieSession from './hooks/useCookieSession'
const PHONE_REG_EXP = /\+?(9[976]\d|8[987530]\d|6[987]\d|5[90]\d|42\d|3[875]\d|2[98654321]\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1)\d{1,14}$/

interface ILoginProps { }

const variants = {
  entrance: (step: number) => {
    return {
      opacity: 0,
      x: 200 * (step > 0 ? 1 : -1)
    }
  },
  animate: {
    opacity: 1,
    x: 0
  },
  exit: (step: number) => {
    return {
      opacity: 0,
      x: 200 * (step > 0 ? -1 : 1)
    }
  }
}

interface IAuthenticateResponse {
  success: boolean
  data: { code: string }
}

interface IRequest {
  status: 'DONE' | 'LOADING' | 'ERROR' | 'STALE'
  data: Record<any, any>
}

interface IToaster {
  type: 'ERROR' | 'SUCCESS' | null
  message: string
  show: boolean
}

interface ILoader {
  show: boolean
  extra?: React.ReactElement
}

const YAC_API_URL = 'https://api-v3.yacchat.com/api'

function useQuery() {
  const params = new URLSearchParams(window.location.search) as any
  return {
    client_id: params.get('client_id'),
    redirect_uri: params.get('redirect_uri'),
    state: params.get('state')
  }
}

const Login: React.FC<ILoginProps> = ({ }) => {
  const query = useQuery()
  const [showModal, setShowModal] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [step, setStep] = useState<number>(0)
  const [request, setRequest] = useState<IRequest>({
    status: 'STALE',
    data: {}
  })
  const [toaster, setToaster] = useState<IToaster>({
    type: null,
    show: false,
    message: ''
  })
  const [loader, setLoader] = useState<ILoader>({ show: false })
  const cookieSession = useCookieSession()

  const sendOtp = async (notifyOnDone?: boolean) => {
    if (!email) {
      setRequest({
        status: 'ERROR',
        data: {
          message: "Email field can't be blank"
        }
      })

      return
    }
    setRequest((state) => ({ ...state, status: 'LOADING' }))

    try {
      let phone
      const isPhoneNumber = PHONE_REG_EXP.test(email)
      if (isPhoneNumber) {
        phone = /\+\d/.test(email) ? email : `+1${email}`
      }
      const res = await axios
        .post(
          `${YAC_API_URL}/v1/users/signin`,
          phone
            ? { phone }
            : {
              email
            }
        )
        .catch((e) => e.response)

      if (res.data.status) {
        setRequest({
          status: 'DONE',
          data: res.data.data
        })
        if (notifyOnDone) {
          setToaster({
            type: 'SUCCESS',
            message: 'New code was sent!',
            show: true
          })
        }
        setStep(1)
      } else {
        setStep(0)
        setRequest({
          status: 'ERROR',
          data: {
            message:
              res.data.statusCode === 400
                ? 'Invalid email format'
                : res.data.message
          }
        })
      }
    } catch (e) {
      const error = e as any;
      setStep(0)
      setRequest({
        status: 'ERROR',
        data: error.data
      })
    }
  }

  const authenticate = async (
    data: { phone?: string; email?: string; otp: string } | { token: string }
  ) => {
    setRequest((state) => ({ ...state, status: 'LOADING' }))
    try {
      const res: AxiosResponse<IAuthenticateResponse> = await axios
        .post<IAuthenticateResponse>(
          `https://integrations.yac.com/api/oauth/authenticate?client_id=${query.client_id}&redirect_uri=${query.redirect_uri}`,
          data
        )
        .catch((err) => {
          throw err.response
        })

      if (res.data.success) {
        setRequest({
          status: 'DONE',
          data: res.data.data
        })
        setLoader({
          show: true,
          extra: (
            <>
              Redirecting you to{' '}
              <a style={{ color: 'black' }} href={query.redirect_uri}>
                {query.redirect_uri}
              </a>
            </>
          )
        })
        const redirect_uri = query.redirect_uri.replace(/\/$/, '')
        setTimeout(() =>
          window.location.replace(
            `${redirect_uri}?code=${res.data.data.code}&state=${query.state}`
          )
        )
      }
    } catch (e) {
      let error = e as any;
      setStep(1)
      if (error.data.message === 'not-on-paid-plan') {
        setEmail('')
        setOtp('')
        setLoader({ show: false })
        setRequest({
          status: 'STALE',
          data: {}
        })
        document.cookie = `token=${error.data.token || ''};domain=.yac.com;path=/`;
        return setShowModal(true);
      }
      setRequest({
        status: 'ERROR',
        data: error?.data || 'Failed to signin, please try again.'
      })
      throw 'request failed'
    }
  }

  const authenticateWithEmail = async () => {
    if (!otp) {
      setRequest({
        status: 'ERROR',
        data: {
          message: "Code field can't be blank"
        }
      })

      return
    }
    if (email) {
      const isPhoneNumber = PHONE_REG_EXP.test(email)
      if (isPhoneNumber) {
        const phone = /\+\d/.test(email) ? email : `+1${email}`
        await authenticate({ phone, otp })
      } else {
        await authenticate({ email, otp })
      }
    }
  }

  const authenticateWithCookie = async () => {
    if (!cookieSession) {
      setRequest({
        status: 'STALE',
        data: {}
      })
      return
    }
    try {
      setLoader({
        show: true,
        extra: <>Checking your session</>
      })
      await authenticate({ token: cookieSession })
    } catch (_) {
      // most probably session is not valid
      setLoader({
        show: false
      })
      setStep(0)
    }
  }

  const email_form = () => (
    <motion.form
      key={'email_form'}
      className={'login__wrapper'}
      variants={variants}
      initial={variants.entrance(step)}
      animate={'animate'}
      transition={{ ease: 'easeIn', duration: 0.1 }}
      exit={'exit'}
    >
      <h2>Sign in</h2>
      <span className={'login__form-description'}>
        Use your Yac account email address or phone number
      </span>
      <Input
        name={'email'}
        value={email}
        onChange={setEmail}
        type={'text'}
        placeholder={'Email or Phone Number'}
      />
      <Button
        loading={request.status === 'LOADING'}
        type={'submit'}
        label={'Continue'}
        onClick={() => sendOtp()}
        className={'login__button'}
      />
    </motion.form>
  )

  const otp_form = () => (
    <motion.form
      key={'otp_form'}
      className={'login__wrapper'}
      variants={variants}
      initial={variants.entrance(step)}
      animate={'animate'}
      transition={{ ease: 'easeIn', duration: 0.1 }}
      exit={'exit'}
    >
      <h2>Sign in</h2>
      <span className={'login__form-description'}>
        We sent a 6 digit code to{' '}
        <strong>
          {PHONE_REG_EXP.test(email) && !/\+\d/.test(email)
            ? `+1${email}`
            : email.toLowerCase()}
        </strong>
        . Enter it below to confirm your{' '}
        {/\+?([^A-Za-z]\d)+/g.test(email) ? 'phone number' : 'email address'}.
      </span>
      <Input
        name={'otp'}
        value={otp}
        onChange={setOtp}
        type={'otp'}
        className={'login__otp-input'}
      />
      <Button
        loading={request.status === 'LOADING'}
        type={'submit'}
        label={'Sign in'}
        onClick={authenticateWithEmail}
        className={'login__button'}
      />
      <span>
        Didn’t receive an email?{' '}
        <i onClick={() => sendOtp(true)}>Send it again.</i>
      </span>
    </motion.form>
  )

  useEffect(() => {
    if (toaster.show)
      setTimeout(
        () => setToaster({ show: false, type: null, message: '' }),
        5000
      )
    if (request.status === 'ERROR' && !toaster.show) {
      setToaster({
        show: true,
        message: request?.data?.message?.message || request?.data?.message,
        type: 'ERROR'
      })
      setRequest({ status: 'STALE', data: {} })
    }
  }, [request, toaster])

  useEffect(() => {
    if (cookieSession) {
      authenticateWithCookie()
    }
  }, [cookieSession])

  return (<>
    {showModal && <Modal close={() => {
      setStep(0)
      setShowModal(false)
    }} />}
    <div className={'login'}>
      {loader?.show && !showModal && <Loader fullscreen>{loader.extra}</Loader>}
      <motion.div
        className={'login__nav'}
        animate={step > 0 ? { y: 0 } : { y: 200 }}
        onClick={() => setStep((step) => step - 1)}
      >
        <div>
          <FaArrowLeft />
          Go Back
        </div>
      </motion.div>
      <div className="login__form">
        <AnimatePresence exitBeforeEnter custom={step}>
          {step === 0 ? email_form() : otp_form()}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {toaster.show && (
          <motion.div
            className={`login__toaster ${toaster.type === 'ERROR' ? 'login__toaster--error' : ''
              } ${toaster.type === 'SUCCESS' ? 'login__toaster--success' : ''}`}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            exit={{ y: -100 }}
            transition={{ ease: 'easeOut', duration: 0.2 }}
          >
            {toaster.type === 'ERROR' ? (
              <FaTimes />
            ) : toaster.type === 'SUCCESS' ? (
              <FaCheck />
            ) : null}
            {toaster.message || 'Network Error'}
          </motion.div>
        )}
      </AnimatePresence>

      <span
        style={{
          textAlign: 'center',
          maxWidth: '80%'
        }}
      >
        Don't have a Yac account.
        <a aria-label={'Go to Yac'} href={'https://app.yac.com/create'}>
          Click Here
        </a>
      </span>
    </div>
  </>
  )
}

export default Login
